import { RefreshMode } from "./types";

export interface IClausionStateStore {
  serviceUrl: string;
  automaticCalculation: boolean;
  refreshMode: RefreshMode;
  isRefreshInProgress: boolean;
  recentServiceUrls: { url: string; addedAt: Date }[];
  isLoggingEnabled: boolean;
  drErrorValue: string | undefined;
  cacheTtl: number; // Cache TTL in minutes (0 = cache disabled, default: 5)
  forceRefreshTimestamp: number; // Timestamp of the last force refresh; used to clear the cache once per action
  auth: {
    accessToken: string | null;
    scope: string | null;
  };
  setServiceUrl: (to: string) => void;
  setAutomaticCalculation: (to: boolean) => void;
  setRefreshMode: (to: RefreshMode) => void;
  setIsRefreshInProgress: (to: boolean) => void;
  addRecentServiceUrl: (to: string) => void;
  delRecentServiceUrl: (to: string) => void;
  setIsLoggingEnabled: (to: boolean) => void;
  setDrErrorValue: (to: string) => void;
  setCacheTtl: (to: number) => void;
  setForceRefreshTimestamp: (to: number) => void;
  setAuth: (auth: { accessToken: string | null; scope: string | null }) => void;
  setAccessToken: (token: string | null) => void;
  setScope: (scope: string | null) => void;
}

const clausionStateStore = (set) => ({
  serviceUrl: "",
  automaticCalculation: false,
  refreshMode: "Sheet",
  isRefreshInProgress: false,
  recentServiceUrls: [],
  isLoggingEnabled: false,
  drErrorValue: "",
  cacheTtl: 5,
  forceRefreshTimestamp: 0,
  auth: {
    accessToken: null,
    scope: null,
  },

  setServiceUrl: (to: string) =>
    set(() => {
      const allowedDomains = [
        "clausion.com",
        "clausion.net",
        "insightsoftware.com",
        "localhost", // For local development
      ];

      // If service URL is empty, return empty string. This makes it possible to remove the service URL from the store.
      if (!to) {
        return { serviceUrl: "" };
      }
      try {
        // Ensure that the URL is prefixed with "https://"
        let formattedUrl = "https://" + to.replace(/^https?:\/\//i, "");

        // Validate url
        let url = new URL(formattedUrl);
        if (!allowedDomains.some((domain) => url.hostname.endsWith(domain))) {
          throw new Error(`URL domain '${to}' is not allowed`);
        }

        // Return the host part of the URL
        return { serviceUrl: url.host };
      } catch (e) {
        set(() => ({ serviceUrl: "" }));
        throw new Error(e.message);
      }
    }),

  setAutomaticCalculation: (to: boolean) =>
    set(() => {
      return { automaticCalculation: to };
    }),

  setRefreshMode: (to: RefreshMode) =>
    set(() => {
      return { refreshMode: to };
    }),

  setIsRefreshInProgress: (to: boolean) =>
    set(() => {
      return { isRefreshInProgress: to };
    }),

  addRecentServiceUrl: (to: string) =>
    set((state) => {
      const existingUrlIndex = state.recentServiceUrls.findIndex((item) => item.url === to);
      if (existingUrlIndex !== -1) {
        const updatedUrls = [...state.recentServiceUrls];
        updatedUrls[existingUrlIndex] = { url: to, addedAt: new Date() };
        return { recentServiceUrls: updatedUrls };
      } else {
        const newUrl = { url: to, addedAt: new Date() };
        return { recentServiceUrls: [...state.recentServiceUrls, newUrl] };
      }
    }),

  delRecentServiceUrl: (to: string) =>
    set((state) => {
      return { recentServiceUrls: state.recentServiceUrls.filter((item) => item.url !== to) };
    }),

  setIsLoggingEnabled: (to: boolean) =>
    set(() => {
      return { isLoggingEnabled: to };
    }),
  setDrErrorValue: (to: string) =>
    set(() => {
      return { drErrorValue: to };
    }),

  setCacheTtl: (to: number) =>
    set(() => {
      return { cacheTtl: to };
    }),

  setForceRefreshTimestamp: (to: number) =>
    set(() => {
      return { forceRefreshTimestamp: to };
    }),

  setAuth: (auth: { accessToken: string | null; scope: string | null }) =>
    set(() => ({ auth })),

  setAccessToken: (token: string | null) =>
    set((state) => ({ auth: { ...state.auth, accessToken: token } })),

  setScope: (scope: string | null) =>
    set((state) => ({ auth: { ...state.auth, scope } })),
});

export default clausionStateStore;
